import { useState, useEffect } from "react";

const UTM_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_id",
  "utm_term",
  "utm_content",
];
const CLICK_ID_PARAMS = ["fbclid", "gclid"];

// Calendly reloads from scratch every time its URL changes, so we collect the
// tracking data first and render it once. These caps keep a slow or missing
// source from holding the calendar back.
const PARENT_URL_TIMEOUT_MS = 1500;
const IP_TIMEOUT_MS = 1500;

const isInIframe = window.self !== window.top;

// The Meta pixel on the parent WordPress site sets _fbc on the shared
// .allbetter.co.il domain, so the iframe document can read it directly even
// though the UTM params themselves must arrive via the parent-URL bridge.
const readFbcCookie = () => {
  const match = document.cookie.match(/(?:^|;\s*)_fbc=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : undefined;
};

const withTimeout = (promise, ms) =>
  Promise.race([promise, new Promise((resolve) => setTimeout(() => resolve(null), ms))]);

const fetchUserIP = async () => {
  try {
    const result = await fetch("https://api.ipify.org?format=json");
    const data = await result.json();
    return data.ip;
  } catch (error) {
    console.error("Error getting user IP:", error);
    return null;
  }
};

const EMPTY_PARENT = {
  parentUrlParams: new URLSearchParams(),
  utmParams: {},
  clickIds: {},
  originalUrl: "",
};

// The parent WordPress page posts its URL into the iframe (see public/index.html);
// the UTMs live on the parent URL, not on the iframe's own URL.
const parseParentUrl = (href) => {
  if (!href) return EMPTY_PARENT;
  try {
    const parentUrl = new URL(href);
    const searchParams = new URLSearchParams(parentUrl.search);
    const collect = (params) =>
      params.reduce((acc, param) => {
        const value = searchParams.get(param);
        if (value) acc[param] = value;
        return acc;
      }, {});
    const fbc = readFbcCookie();
    return {
      parentUrlParams: searchParams,
      utmParams: collect(UTM_PARAMS),
      clickIds: { ...collect(CLICK_ID_PARAMS), ...(fbc ? { fbc } : {}) },
      originalUrl: parentUrl.href,
    };
  } catch (error) {
    console.error("Error getting parent URL:", error);
    return EMPTY_PARENT;
  }
};

// Returns `ready: false` until the parent URL and the visitor's IP are in (or
// their caps ran out); render Calendly only once it is true.
const useUTMParams = () => {
  const [state, setState] = useState({ ready: false, ipAddress: "", ...EMPTY_PARENT });

  useEffect(() => {
    if (!isInIframe) {
      // Opened directly: there is no parent page to wait for.
      withTimeout(fetchUserIP(), IP_TIMEOUT_MS).then((ip) =>
        setState((prev) => ({ ...prev, ready: true, ipAddress: ip || "" }))
      );
      return;
    }

    const parentUrlPromise = window.parentUrlPromise.then(() => window.parentUrl);
    Promise.all([
      withTimeout(parentUrlPromise, PARENT_URL_TIMEOUT_MS),
      withTimeout(fetchUserIP(), IP_TIMEOUT_MS),
    ]).then(([, ip]) =>
      setState({ ready: true, ipAddress: ip || "", ...parseParentUrl(window.parentUrl) })
    );

    // A parent URL that arrives after its cap still matters for attribution, so
    // it is applied even though Calendly then reloads once more.
    parentUrlPromise.then((href) =>
      setState((prev) =>
        prev.ready && prev.originalUrl !== href ? { ...prev, ...parseParentUrl(href) } : prev
      )
    );
  }, []);

  return state;
};

export default useUTMParams;
